import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable, Injector, inject } from "@angular/core";
import { LvfRequestSource } from "@kiraah-shared/common";
import { Observable, throwError } from "rxjs";
import { catchError, finalize, switchMap } from "rxjs/operators";
import { AuthentificationService } from "./authentification.service";
import { SessionActivityCoordinatorService } from "./session-activity-coordinator.service";

/**
 * HTTP Interceptor that handles authentication token management.
 * 
 * Features:
 * - Adds withCredentials for auth endpoints (cookie support)
 * - Handles 401 errors by attempting token refresh
 * - Runs opportunistic refresh for near-expiry business requests
 * - Properly handles auth failures with cleanup
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private static readonly REQUEST_SRC_HEADER = "X-REQUEST-SRC";
  private static readonly API_CALL_TYPE_HEADER = "X-API-CALL-TYPE";
  // Use Injector for lazy injection to avoid circular dependency
  // AuthentificationService -> ServiceBase -> HttpClient -> AuthInterceptor -> AuthentificationService
  private injector = inject(Injector);
  private _authService?: AuthentificationService;
  private _sessionActivityCoordinator?: SessionActivityCoordinatorService;

  private get authService(): AuthentificationService {
    if (!this._authService) {
      this._authService = this.injector.get(AuthentificationService);
    }
    return this._authService;
  }

  private get sessionActivityCoordinator(): SessionActivityCoordinatorService {
    if (!this._sessionActivityCoordinator) {
      this._sessionActivityCoordinator = this.injector.get(SessionActivityCoordinatorService);
    }
    return this._sessionActivityCoordinator;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.authService.isLoggingOut && !this.isAuthEndpoint(req.url) && !this.isPublicEndpoint(req)) {
      return throwError(() => new HttpErrorResponse({
        status: 0,
        statusText: 'Logout in progress',
        url: req.url
      }));
    }

    // Add withCredentials for auth endpoints to send HttpOnly cookies
    let modifiedReq = req;
    if (this.isAuthEndpoint(req.url)) {
      modifiedReq = req.clone({ withCredentials: true });
    }
    modifiedReq = this.withWebRequestSourceHeader(modifiedReq);

    const shouldTrackRequest = this.shouldTrackUserActivity(modifiedReq);
    if (shouldTrackRequest) {
      this.sessionActivityCoordinator.beginTrackedRequest();
      this.sessionActivityCoordinator.recordBackendActivity();
    }

    const downstreamRequest$ = shouldTrackRequest
      ? this.authService.refreshTokenIfNearExpiry().pipe(
        catchError((error) => {
          this.handleAuthFailure();
          return throwError(() => error);
        }),
        switchMap(() => next.handle(modifiedReq))
      )
      : next.handle(modifiedReq);

    return downstreamRequest$.pipe(
      catchError((error: HttpErrorResponse) => {
        // Only handle 401 errors for non-auth endpoints
        if (error.status !== 401 || this.isAuthEndpoint(req.url) || this.isPublicEndpoint(req)) {
          return throwError(() => error);
        }

        // If user is not authenticated, don't attempt refresh
        if (!this.authService.authenticated) {
          console.debug('[AuthInterceptor] User not authenticated, skipping refresh');
          return throwError(() => error);
        }

        return this.authService.refreshToken().pipe(
          switchMap(response => {
            if (response.IsSuccessful && response.Data?.AccessToken) {
              this.sessionActivityCoordinator.recordBackendActivity();
              const updatedReq = this.withWebRequestSourceHeader(req).clone({
                setHeaders: { Authorization: `Bearer ${response.Data.AccessToken}` }
              });
              return next.handle(updatedReq);
            }

            this.handleAuthFailure();
            return throwError(() => error);
          }),
          catchError(refreshError => {
            console.warn('[AuthInterceptor] Token refresh failed:', refreshError?.message || 'Unknown error');
            this.handleAuthFailure();
            return throwError(() => refreshError);
          })
        );
      }),
      finalize(() => {
        if (shouldTrackRequest) {
          this.sessionActivityCoordinator.endTrackedRequest();
        }
      })
    );
  }

  /**
   * Handles authentication failure by cleaning up tokens and user state.
   */
  private handleAuthFailure(): void {
    this.authService.handleAuthenticationFailure('interceptor_401');
  }

  /**
   * Checks if the URL is an authentication endpoint.
   * These endpoints should not trigger token refresh on 401.
   */
  private isAuthEndpoint(url: string): boolean {
    // /me is intentionally excluded: it requires a valid token and should
    // benefit from automatic token refresh on 401, unlike login/refresh/etc.
    return url.includes("/api/master/authentication/login")
      || url.includes("/api/master/authentication/refresh")
      || url.includes("/api/master/authentication/mfa/verify")
      || url.includes("/api/master/authentication/select-tenant")
      || url.includes("/api/master/authentication/logout")
      || url.includes("/api/master/authentication/forgot-password")
      || url.includes("/api/master/authentication/reset-password")
      || url.includes("/api/master/authentication/verify-reset-token");
  }

  /**
   * Checks if the URL is a public endpoint that must never trigger auth refresh.
   */
  private isPublicEndpoint(req: HttpRequest<any>): boolean {
    const url = req.url;
    if (url.includes('/api/core/signaturerequests/public')
      || url.includes('/api/consulting/quotation-requests/public')
      || url.includes('/api/sales/supplierquotationsubmissions/public')
      || url.includes('/api/academy/learning-campaigns/public')
      || url.includes('/api/public/engagements')) {
      return true;
    }

    return !!req.headers.get('X-Site-Api-Key')?.trim();
  }

  /**
   * Ensure web runtime requests are consistently tagged at interceptor level.
   * This avoids relying on individual service implementations.
   */
  private withWebRequestSourceHeader(request: HttpRequest<any>): HttpRequest<any> {
    if (this.isPublicEndpoint(request)) {
      return request;
    }

    if (request.headers.get(AuthInterceptor.REQUEST_SRC_HEADER) === `${LvfRequestSource.WEB}`) {
      return this.withInternalApiCallTypeHeader(request);
    }

    const requestWithSource = request.clone({
      setHeaders: {
        [AuthInterceptor.REQUEST_SRC_HEADER]: `${LvfRequestSource.WEB}`
      }
    });

    return this.withInternalApiCallTypeHeader(requestWithSource);
  }

  private withInternalApiCallTypeHeader(request: HttpRequest<any>): HttpRequest<any> {
    if (request.headers.get(AuthInterceptor.API_CALL_TYPE_HEADER) === `${LvfRequestSource.INTERNAL_API}`) {
      return request;
    }

    return request.clone({
      setHeaders: {
        [AuthInterceptor.API_CALL_TYPE_HEADER]: `${LvfRequestSource.INTERNAL_API}`
      }
    });
  }

  /**
   * Updates the inactivity timestamp for user-driven HTTP requests.
   */
  private shouldTrackUserActivity(req: HttpRequest<any>): boolean {
    if (!this.authService.authenticated || this.authService.isLoggingOut) {
      return false;
    }

    if (this.isAuthEndpoint(req.url) || this.isPublicEndpoint(req) || req.url.includes('/assets/')) {
      return false;
    }

    return true;
  }
}
